import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

import { useCallback, useEffect, useState } from 'react';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { CircularProgress } from '@classity-edtech/ui-base';
import {
  useCreateFreeFormEventLogMutation,
  useGetFreeFormEventLogsQuery,
} from '../../services/eventLogs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { Container, Stack } from '@mui/system';
import {
  Calendar,
  Cancel,
  Divider,
  FullLogo,
  UnsupportedIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Typography } from '@mui/material';
import { useGetProfileQuery, useGetUserQuery } from '../../services/profile';
import { AlertComp } from '@classity-edtech/ui-base/dist/components/Displays/AlertComp/AlertComp';
import { Heading } from '@classity-edtech/ui-base/dist/components/Displays/Heading/Heading';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { Email, WhatsAppIcon } from '@classity-edtech/ui-base/dist/components/Displays';
import {
  EventAction,
  EventSourceName,
  NotificationLabel,
  Roles,
  ServiceName,
  SeverityLevel,
} from '@classity-edtech/platform-utils';
import { Profile } from '../../services/models/profile';
import Helmet from '../../components/Helmet';
import { useGetResponsesByIDQuery, useUpdateResponseMutation } from '../../services/response';
import { Response } from '../../services/models/response';
import { Notification, User } from '../../services/models/authentication';
import { useGetMyProfileQuery } from '../../services/users';

export interface HomeProps extends MuiBoxProps {}

type DateStatus = 'LESS_THAN_24_HOURS' | 'MORE_THAN_24_HOURS' | 'PAST';

export const InterviewConfirmation = ({ className }: HomeProps) => {
  const email = 'support@classity.co';
  const phone = '2348065957778';
  const screens = useAppMediaQuery();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const params = useParams();

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const {
    data: eventLogs,
    isLoading: loadingEventLogs,
    isError: eventLogsError,
  } = useGetFreeFormEventLogsQuery(
    {
      correlationId: params.correlationId,
      page: 1,
      limit: 1,
    },
    { skip: !user?.id },
  );
  const { data: adminUser } = useGetUserQuery(
    { userId: eventLogs?.[0]?.data?.adminId },
    { skip: !eventLogs?.[0]?.data?.adminId },
  );

  const { data: admin } = useGetProfileQuery(
    {
      userId: eventLogs?.[0]?.data?.adminId,
    },
    { skip: !eventLogs?.[0]?.data?.adminId },
  );

  const { data: response } = useGetResponsesByIDQuery(
    {
      responseId: eventLogs?.[0]?.data?.responseId,
      formName: 'ONBOARDING_TUTOR', // key of the FreeFormName enum, which is the form name for the onboarding tutor form
    },
    { skip: !eventLogs?.[0]?.data?.responseId },
  );
  const [updateResponse] = useUpdateResponseMutation();

  const [createEvent] = useCreateFreeFormEventLogMutation();

  const handleConfirmationEventLog = async () => {
    const eventLogInputs = {
      level: SeverityLevel.INFO,
      action: EventAction.CREATE,
      source: {
        name: EventSourceName.EVENT,
        service: ServiceName.WWW,
      },
      message: 'Interview date confirmation',
      data: {
        ...eventLogs?.[0]?.data,
        createdAt: new Date()?.toISOString(),
      },
    };

    await createEvent(eventLogInputs);
  };

  const navigateHome = () => {
    navigate('/');
  };

  const handleConfirmationEmail = async ({
    type,
    interviewDate,
    userFirstName,
    userEmail,
    userLastName,
    adminEmail,
    adminFirstName,
    adminLastName,
    interviewLocation,
    notification,
  }: {
    type: 'tutor' | 'admin';
    interviewDate: string | Date | { date: string; time: string };
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    adminEmail: string;
    adminFirstName: string;
    adminLastName: string;
    interviewLocation: string;
    notification: Notification;
  }) => {
    const date =
      typeof interviewDate === 'object' && 'date' in interviewDate
        ? dayjs(interviewDate.date)
        : dayjs(interviewDate);

    const emailInput = {
      fromField: 'mail@classity.co',
      subscribers: [type === 'tutor' ? userEmail : adminEmail],
      template:
        type === 'tutor'
          ? 'email.interview.tutor.confirmation.classity.co'
          : 'email.interview.classity.confirmation.classity.co',
      markers:
        type === 'tutor'
          ? {
              EMAIL_HELP: 'support@classity.co',
              INTERVIEWER_NAME: `${adminFirstName} ${adminLastName}`,
              APPLICANT_NAME: `${userFirstName} ${userLastName}`,
              APPOINTMENT_LINK_1: interviewLocation,
              APPOINTMENT_DATE: date.format('MMMM D, YYYY'),
              APPOINTMENT_TIME: date.format('h:mm A'),
              APPOINTMENT_LOCATION: interviewLocation,
              APPOINTMENT_LENGTH: '1h 30min',
            }
          : {
              EMAIL_HELP: 'support@classity.co',
              INTERVIEWER_NAME: `${adminFirstName} ${adminLastName}`,
              PARTICIPANT_NAME_1: `${userFirstName} ${userLastName}`,
              APPOINTMENT_LINK_1: interviewLocation,
              APPOINTMENT_DATE: date.format('MMMM D, YYYY'),
              APPOINTMENT_TIME: date.format('h:mm A'),
              APPOINTMENT_LOCATION: interviewLocation,
              APPOINTMENT_LENGTH: '1h 30min',
            },

      notification,
    };

    await createEvent({
      level: SeverityLevel.CRITICAL,
      action: EventAction.EMAIL,
      source: {
        name: EventSourceName.JOB,
        service: ServiceName.WWW,
      },
      message: 'Interview confirmation Email',
      data: emailInput,
    });
  };

  const handleSuccess = useCallback(
    async ({
      data,
      admin,
      response,
      user,
      adminUser,
    }: {
      data: any;
      admin: Profile;
      response: Response;
      user: User;
      adminUser: User;
    }) => {
      await handleConfirmationEventLog();

      const emailArg = {
        interviewDate: data?.interviewDate,
        adminEmail: adminUser?.email,
        userEmail: user?.email,
        userFirstName: data?.userFirstName,
        userLastName: data?.userLastName,
        adminFirstName: admin?.firstname,
        adminLastName: admin?.lastname,
      };

      await Promise.all([
        handleConfirmationEmail({
          ...emailArg,
          type: 'admin',
          interviewLocation: response?.data?.interviewLocation,
          notification: adminUser?.preferences?.notifications?.find(
            n => n?.medium === NotificationLabel.EMAIL,
          ),
        }),
        handleConfirmationEmail({
          ...emailArg,
          type: 'tutor',
          interviewLocation: response?.data?.interviewLocation,
          notification: user?.preferences?.notifications?.find(
            n => n?.medium === NotificationLabel.EMAIL,
          ),
        }),
      ]);

      updateResponse({
        responseId: response?.id,
        responseInput: {
          report: response?.report,
          formName: response?.formName,
          status: response?.status,
          data: {
            ...response?.data,
            interviewDates: response?.data?.interviewDates?.map(p => {
              if (p?.correlationId === params.correlationId) {
                return {
                  ...p,
                  confirmed: user?.roleId === Roles.PROSPECT_TUTOR || user?.roleId === Roles.TUTOR,
                };
              }

              return {
                ...p,
                confirmed: false,
              };
            }),
          },
        },
      });

      setShowSuccessScreen(true);
    },
    [],
  );

  const getInterviewDateStatus = (): DateStatus => {
    const interviewDateData = eventLogs?.[0]?.data?.interviewDate;
    const interviewDate =
      typeof interviewDateData === 'object' && interviewDateData?.date
        ? new Date(interviewDateData.date)
        : new Date(interviewDateData);
    const now = new Date();
    const diffInMs = interviewDate.getTime() - now.getTime();

    if (diffInMs < 0) {
      return 'PAST';
    }

    const hours24InMs = 24 * 60 * 60 * 1000;

    if (diffInMs <= hours24InMs) {
      return 'LESS_THAN_24_HOURS';
    }

    return 'MORE_THAN_24_HOURS';
  };

  const getFormattedInterviewDate = () => {
    const interviewDateData = eventLogs?.[0]?.data?.interviewDate;
    if (typeof interviewDateData === 'object' && interviewDateData?.date) {
      return dayjs(interviewDateData.date);
    }
    return dayjs(interviewDateData);
  };

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      localStorage.removeItem('token');
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  useEffect(() => {
    const run = async () => {
      if (getInterviewDateStatus() === 'PAST') {
        return navigate(`/404`, { replace: true });
      }
      if (getInterviewDateStatus() === 'LESS_THAN_24_HOURS') {
        return setShowErrorScreen(true);
      }
      if (getInterviewDateStatus() === 'MORE_THAN_24_HOURS') {
        return await handleSuccess({
          data: eventLogs?.[0]?.data,
          admin,
          adminUser,
          user,
          response,
        });
      }
    };

    if (
      !loadingEventLogs &&
      !eventLogsError &&
      !!eventLogs?.[0]?.data?.interviewDate &&
      !!admin?.firstname &&
      !!user &&
      !!adminUser &&
      !!response
    ) {
      run();
    }
  }, [loadingEventLogs, eventLogsError, eventLogs, admin, adminUser, user, response]);

  return (
    <>
      <Helmet />
      {!(showErrorScreen || showSuccessScreen) ? (
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {showErrorScreen && (
            <Container className={className} sx={{ px: { xs: 2.5, md: 2 } }}>
              <Box sx={{ py: { xs: 1, md: 2 } }}>
                <FullLogo
                  sx={{ width: { xs: '82px', md: '140px' }, height: 'auto', cursor: 'pointer' }}
                  onClick={navigateHome}
                />
              </Box>
              <Stack
                sx={{
                  width: { xs: '100%', md: '568px' },
                  my: { xs: 5, md: 10 },
                  mx: 'auto',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <UnsupportedIcon sx={{ width: '170px', height: '130px' }} />
                <Typography variant='subtitle3' sx={{ mt: 3 }}>
                  Interview invitation has expired
                </Typography>
                <Typography variant='body2' sx={{ mb: 2 }}>
                  The invitation link for your scheduled interview with Classity has expired. To
                  ensure you can still join the interview, please contact us as soon as possible so
                  we can provide you with a new link or assist you with alternative arrangements.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 1,
                      gap: 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => window.open(`mailto:${email}`, '_blank')}
                  >
                    <Email sx={{ width: '16px', height: '16px' }} />
                    <Typography variant='body3'>{email}</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 1,
                      gap: 1,
                      cursor: 'pointer',
                    }}
                    onClick={() => window.open(`https://wa.me/${phone}`, '_blank')}
                  >
                    <WhatsAppIcon sx={{ width: '16px', height: '16px' }} />
                    <Typography variant='body3'>{phone}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Container>
          )}
          {showSuccessScreen && (
            <Container className={className} sx={{ px: { xs: 2.5, md: 2 } }}>
              <Box
                sx={{
                  py: { xs: 1, md: 2 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <FullLogo
                  sx={{ width: { xs: '82px', md: '140px' }, height: 'auto', cursor: 'pointer' }}
                  onClick={navigateHome}
                />

                <Cancel sx={{ width: '20px', height: '20px', cursor: 'pointer' }} />
              </Box>

              <Divider />

              <Box sx={{ width: { xs: '100%', md: '568px' }, my: { xs: 3, md: 6 }, mx: 'auto' }}>
                <Typography
                  variant={screens.isMobile ? 'subtitle5' : 'subtitle3'}
                  sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
                >
                  Your interview appointment is confirmed
                </Typography>
                <Typography
                  variant={screens.isMobile ? 'body3' : 'body2'}
                  sx={{
                    maxWidth: '500px',
                    textAlign: { md: 'center' },
                    display: 'block',
                    mb: { xs: 3, md: 4 },
                    mx: 'auto',
                  }}
                >
                  Your interview is confirmed and will take place on{' '}
                  <strong>{`${getFormattedInterviewDate().format('ddd Do [of] MMMM YYYY [at] hh:mma')} (WAT)`}</strong>
                  . Check your email{' '}
                  <span style={{ textDecoration: 'underline' }}>
                    {eventLogs?.[0]?.data?.userEmail}
                  </span>{' '}
                  for further details.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    gap: 3,
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Box sx={{ flex: 0.5 }}>
                    <AlertComp
                      icon={
                        <Calendar
                          sx={theme => ({
                            width: '24px',
                            Height: '24px',
                            color: globalColours['$global-colour-base-contrast'],
                          })}
                        />
                      }
                      sentiment='success'
                      titleElement='Interview with classity'
                      description={`${getFormattedInterviewDate().format('ddd Do [of] MMMM YYYY [at] hh:mma')} (WAT)`}
                      ctaLabel=''
                      ctaAction={() => {}}
                      hideCtaIcon
                    />
                  </Box>
                  <Stack sx={{ display: 'flex', gap: 2, flex: 0.5 }}>
                    <Heading
                      heading='What to expect'
                      subHeading='During the interview, we will discuss your background, teaching philosophy, and approach to tutoring math'
                    />
                    <Heading
                      heading='Demo lesson'
                      subHeading='You will be expected to conduct a short demonstration lesson with one of our students'
                    />
                    <Heading
                      heading='Verification'
                      subHeading='Following a successful interview, we will move ahead to complete the educational verification, reference and background checks'
                    />
                  </Stack>
                </Box>
              </Box>
            </Container>
          )}
        </>
      )}
    </>
  );
};

InterviewConfirmation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

InterviewConfirmation.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(InterviewConfirmation)(
  ({ theme }) => `
`,
);
